// Envío del cuestionario de feedback + premio de imágenes — flujo migrado de
// n8n al backend nuevo (ADR-684).
//
// Flag de rollback (VITE_USE_ATLAS_BACKEND):
//  • ON  → POST {ATLAS_BACKEND_URL}/v1/feedback/cuestionario con JWT de Supabase.
//          El backend hace TODO en una transacción: insert de la respuesta +
//          acreditar las imágenes + marcar `cuestionario_respondido`. Devuelve
//          cuántas imágenes acreditó realmente (el monto lo decide el servidor).
//  • OFF → comportamiento viejo EXACTO: el cliente inserta la fila con
//          supabase-js y dispara el webhook de n8n, que acredita y marca la
//          bandera. Se conserva tal cual —incluido su fallo silencioso— porque
//          es la vía de rollback y tiene que comportarse como antes.
//
// Por qué se movió el insert al backend: antes eran tres pasos con dos actores y
// sin atomicidad (front inserta → webhook acredita y marca). Si el webhook
// fallaba quedaba una respuesta guardada SIN premio y, como la bandera no se
// marcaba, al usuario le volvía a aparecer el banner y podía responder otra vez,
// duplicando filas. Ahora o pasan las tres cosas o no pasa ninguna.

import { supabase } from './supabase';
import { atlasBackendFetch } from './atlasBackend';
import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';

const N8N_REWARD_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/4a7bff42-ee96-4579-9362-068f8567f608';

/** Premio esperado. El backend manda el real en la respuesta; esto es el fallback. */
export const FREE_IMAGES_REWARD = 10;

export interface CuestionarioRespuestas {
  tiendaOnline: string;
  publicidad: string[];
  rolEmpresa: string;
  funcionDeseada: string;
  mejoraSugerida: string;
}

export type CuestionarioSubmitResult =
  /** Guardado y premio acreditado. `imagesCredited` es lo que dijo el servidor. */
  | { status: 'ok'; imagesCredited: number }
  /** La empresa ya había respondido: no se re-acredita, pero el banner debe irse. */
  | { status: 'already_answered' }
  /** Nada quedó guardado — el usuario puede reintentar. */
  | { status: 'error'; message: string };

const ERROR_GENERICO =
  'No pudimos guardar tu respuesta. Probá de nuevo en unos segundos.';

export async function submitCuestionario(
  idCompany: string,
  r: CuestionarioRespuestas,
): Promise<CuestionarioSubmitResult> {
  if (USE_ATLAS_BACKEND) {
    let res: Response;
    try {
      res = await atlasBackendFetch('/v1/feedback/cuestionario', {
        tienda_online: r.tiendaOnline,
        publicidad: r.publicidad,
        rol_empresa: r.rolEmpresa.trim(),
        funcion_deseada: r.funcionDeseada.trim() || null,
        mejora_sugerida: r.mejoraSugerida.trim() || null,
      });
    } catch {
      return { status: 'error', message: ERROR_GENERICO };
    }

    if (res.status === 409) return { status: 'already_answered' };

    if (!res.ok) {
      // 400/500: el backend hizo rollback, no quedó nada a medias.
      return { status: 'error', message: ERROR_GENERICO };
    }

    let credited = FREE_IMAGES_REWARD;
    try {
      const json = (await res.json()) as { imagenes_acreditadas?: number };
      if (typeof json?.imagenes_acreditadas === 'number') {
        credited = json.imagenes_acreditadas;
      }
    } catch {
      /* body raro pero 200: el premio se acreditó igual, usamos el default */
    }
    return { status: 'ok', imagesCredited: credited };
  }

  // ── Rollback: comportamiento viejo, tal cual estaba ────────────────────────
  try {
    const { error: insertErr } = await supabase.from('cuestionarios').insert({
      id_company: idCompany,
      tienda_online: r.tiendaOnline,
      publicidad: r.publicidad,
      rol_empresa: r.rolEmpresa.trim() || null,
      funcion_deseada: r.funcionDeseada.trim() || null,
      mejora_sugerida: r.mejoraSugerida.trim() || null,
    });
    if (insertErr) throw insertErr;

    // El webhook acredita y marca la bandera. Se ignora su resultado igual que
    // antes: la respuesta ya quedó guardada y el equipo puede acreditar a mano.
    try {
      await fetch(N8N_REWARD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({ id_company: idCompany }),
      });
    } catch (webhookErr) {
      console.warn(
        '[cuestionario] Reward webhook falló (encuesta ya guardada):',
        webhookErr,
      );
    }
    return { status: 'ok', imagesCredited: FREE_IMAGES_REWARD };
  } catch (err: any) {
    return { status: 'error', message: err?.message || ERROR_GENERICO };
  }
}
